import React, { useContext, useEffect, useState } from 'react';
import ResultContext from '../context/ResultContext';
import Answer from '../components/Answer';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/Result.css';
import Spinner from '../components/Spinner';
import Categories from '../components/Categories';
import Navbar from '../components/Navbar';
import InfeedAd from '../components/InfeedAd';
import html2pdf from 'html2pdf.js';

export default function Result() {
  const formatDate = (timestamp) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const compareDates = (a, b) => {
    return new Date(b.date) - new Date(a.date);
  };

  const navigate = useNavigate();
  const HOST = process.env.REACT_APP_HOST_NAME;
  const [quizes, setQuizes] = useState(null);
  const { quizData, score, responses } = useContext(ResultContext);

  const fetchData = async () => {
    try {
      const response = await fetch(HOST + 'api/getallquizes', {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch quizzes');
      }

      const data = await response.json();
      const sortedQuizzes = data.sort(compareDates);
      
      // Filter out current quiz if quizData exists
      if (quizData) {
        setQuizes(sortedQuizzes.filter(quiz => quiz._id !== quizData._id));
      } else {
        setQuizes(sortedQuizzes);
      }
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [quizData]);

  // Function to download the current webpage as a PDF
  const downloadPdf = () => {
    const element = document.getElementById('result-container');
    const opt = {
      margin: 10,
      filename: `quiz-result-${formatDate(quizData.date)}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save();
  };

  // Calculate percentage
  const percentage = quizData ? ((score / quizData.questions.length) * 100).toFixed(2) : 0;

  // Determine performance level
  const getPerformanceLevel = () => {
    if (percentage >= 80) return { text: 'Excellent! 🎉', color: '#28a745' };
    if (percentage >= 60) return { text: 'Good Job! 👍', color: '#17a2b8' };
    if (percentage >= 40) return { text: 'Keep Practicing! 💪', color: '#ffc107' };
    return { text: 'Need More Practice 📚', color: '#dc3545' };
  };

  const performance = getPerformanceLevel();

  // Render a message if quizData is not yet loaded or responses are empty
  if (!quizData || responses.length === 0) {
    return (
      <>
        <Navbar />
        <div className="center-container">
          <div className="no-result-card">
            <h2>No Quiz Result Found</h2>
            <p>Please complete a quiz first to see your results.</p>
            <button 
              onClick={() => navigate('/subjects')}
              className="btn btn-primary"
            >
              Browse Quizzes
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="result-page">
        {/* Result Header */}
        <div className="result-header">
          <div className="container">
            <h1 className="result-heading">📊 Quiz Result</h1>
            <p className="batch-name">
              Subject: <strong>{quizData.subject}</strong>
            </p>
            <p className="quiz-date">Date: {formatDate(quizData.date)}</p>
          </div>
        </div>

        <div className="result-container" id="result-container">
          {/* Score Card */}
          <div className="score-card">
            <div className="score-main">
              <h2 className="score-text">Your Score</h2>
              <div className="score-display">
                <span className="score-value">{score * 2}</span>
                <span className="score-total">/ {quizData.questions.length * 2}</span>
              </div>

              <div className="percentage-display">
                <div className="percentage-circle" style={{ borderColor: performance.color }}>
                  <span style={{ color: performance.color }}>{percentage}%</span>
                </div>
              </div>

              <p className="performance-text" style={{ color: performance.color }}>
                {performance.text}
              </p>
            </div>

            <div className="score-stats">
              <div className="stat-item">
                <span className="stat-label">Total Questions</span>
                <span className="stat-value">{quizData.questions.length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Correct Answers</span>
                <span className="stat-value correct">{score}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Wrong Answers</span>
                <span className="stat-value wrong">{quizData.questions.length - score}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Accuracy</span>
                <span className="stat-value">{percentage}%</span>
              </div>
            </div>

            <div className="action-buttons">
              <button onClick={downloadPdf} className="btn btn-success">
                📥 Download PDF
              </button>
              <button 
                onClick={() => navigate('/subjects')}
                className="btn btn-primary"
              >
                📝 More Quizzes
              </button>
              <button 
                onClick={() => navigate(`/start/${quizData._id}`)}
                className="btn btn-info"
              >
                🔄 Retake Quiz
              </button>
            </div>
          </div>

          {/* Answers Section */}
          <div className="answers-section">
            <h2 className="section-title">📋 Detailed Solutions</h2>
            <div className="quiz-container">
              {quizData.questions.map((question, id) => (
                <React.Fragment key={id}>
                  <div className="question-container">
                    <Answer
                      question={question}
                      id={id}
                      selected={responses[id]}
                      answer={quizData.questions[id].answer}
                    />
                  </div>
                  {id !== 0 && id % 6 === 0 && <InfeedAd />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* More Quizzes Section */}
          {quizes && quizes.length > 0 && (
            <div className="more-quizzes-section">
              <h2 className="section-title">🎯 More Quizzes for You</h2>
              {!quizes ? (
                <Spinner />
              ) : (
                <div className="quizzes-list">
                  {quizes.slice(0, 7).map(quiz => (
                    <Link 
                      to={`/start/${quiz._id}`} 
                      key={quiz._id} 
                      className="quiz-link"
                    >
                      <div className="quiz-item">
                        <div className="quiz-info">
                          <strong className="quiz-subject">{quiz.subject}</strong>
                          <span className="quiz-date">{formatDate(quiz.date)}</span>
                        </div>
                        <div className="quiz-meta">
                          <span className="question-count">
                            {quiz.questions?.length || 0} Questions
                          </span>
                          <span className="start-arrow">→</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          <InfeedAd />
          <Categories />
        </div>
      </div>
    </>
  );
}
